import { CommandKind } from '../commands/CommandBuffer';
import {
  PARTICLE_GRAVITY, SLIME_SEPARATION_CORRECTION, SLIME_SEPARATION_IMPULSE, SLIME_SEPARATION_SLACK,
} from '../core/constants';
import type { GameContext } from '../core/GameContext';

export class PhysicsSystem {
  private context!: GameContext;
  private currentSlime = -1;
  private dt = 0;
  private readonly resolveNeighbour = (other: number): void => {
    const { slimes } = this.context; const id = this.currentSlime;
    if (other <= id || slimes.active[other] === 0 || slimes.isDying(other)) return;
    const dx = slimes.x[other] - slimes.x[id]; const dy = slimes.y[other] - slimes.y[id];
    const minDistance = slimes.radius[id] + slimes.radius[other] - SLIME_SEPARATION_SLACK;
    const distanceSquared = dx * dx + dy * dy;
    if (distanceSquared <= 0 || distanceSquared >= minDistance * minDistance) return;
    const distance = Math.sqrt(distanceSquared);
    const overlap = (minDistance - distance) * SLIME_SEPARATION_CORRECTION;
    // 顺手输出接触深度。双方各记一份完整重叠量，与原型一致（a.comp += overlap; b.comp += overlap）。
    slimes.contactDepth[id] += minDistance - distance;
    slimes.contactDepth[other] += minDistance - distance;
    slimes.recordContact(id, other);
    const nx = dx / distance; const ny = dy / distance;
    const impulse = overlap * SLIME_SEPARATION_IMPULSE * this.dt;
    slimes.x[id] -= nx * overlap; slimes.y[id] -= ny * overlap;
    slimes.vx[id] -= nx * impulse; slimes.vy[id] -= ny * impulse;
    slimes.x[other] += nx * overlap; slimes.y[other] += ny * overlap;
    slimes.vx[other] += nx * impulse; slimes.vy[other] += ny * impulse;
  };

  update(context: GameContext, dt: number): void {
    this.context = context; this.dt = dt;
    const { slimes, drops, particles, grid, state, level, commands, catalog } = context;
    const { gravity, fallDamping, horizontalDrag, wallContactDepth, width, height } = level.world;
    state.levelTime += dt; state.steps += 1; state.comboTimer -= dt; if (state.comboTimer <= 0) state.combo = 0;
    slimes.contactDepth.fill(0); slimes.contactCount = 0;
    for (let id = 0; id < slimes.capacity; id += 1) {
      if (slimes.active[id] === 0 || slimes.isDying(id)) continue;
      const definition = catalog.slimes[slimes.definition[id]];
      // 单一下落模型：重力加速、阻尼收敛到终端速度 gravity / -ln(fallDamping)。
      // 原实现按 Falling 状态位分成「下压」与「掉落」两态，那是锚点坍塌的产物，已随之移除。
      // 落速 = 重力 × 定义级落速倍率（种类）× 每只的落速倍率（波次）× 空场提速倍率。
      slimes.vy[id] += gravity * definition.fallScale * slimes.fallScale[id] * state.paceMultiplier * state.fallOverride * dt;
      slimes.vx[id] *= Math.pow(horizontalDrag, dt);
      slimes.vy[id] *= Math.pow(fallDamping, dt);
      const wasInside = slimes.y[id] >= slimes.radius[id];
      slimes.x[id] += slimes.vx[id] * dt; slimes.y[id] += slimes.vy[id] * dt;
      const clampedX = Math.max(slimes.radius[id], Math.min(width - slimes.radius[id], slimes.x[id]));
      if (clampedX !== slimes.x[id]) { slimes.x[id] = clampedX; slimes.contactDepth[id] += wallContactDepth; }
      // 天花板：只挡「本步从场内被推出上边」的。没有它，气泡（质量 0.35 再乘托举）几下就被托出画面上方，
      // 仍算「空中」，通关与提速都得等它飘回来。投放从画面上方入场（entryY < 0）时本来就在场外，
      // 入场途中被分离力向上一推也不能把它顶回边缘，所以看的是「上一步是否已在场内」。
      if (wasInside && slimes.y[id] < slimes.radius[id]) {
        slimes.y[id] = slimes.radius[id]; if (slimes.vy[id] < 0) slimes.vy[id] = 0; slimes.contactDepth[id] += wallContactDepth;
      }
      slimes.scaleVelocity[id] += (1 - slimes.scaleX[id]) * definition.spring * dt;
      slimes.scaleVelocity[id] *= Math.pow(definition.damping, dt);
      slimes.scaleX[id] += slimes.scaleVelocity[id] * dt; slimes.scaleY[id] = 1 / Math.max(0.62, slimes.scaleX[id]);
      // 没有地面。越线的在红线处结算并爆开（core:leak-budget）；这里只是兜底回收，防止没有该规则的关卡漏到画面外。
      if (slimes.y[id] - slimes.radius[id] > height) {
        commands.lifecycle.push(CommandKind.DespawnSlime, -1, id);
        continue;
      }
      // 查询半径按「本体半径 + 当前最大半径」计算。半径随体积变化，写死常量会让超过
      // 该常量的史莱姆静默失去分离——旧实现写死 44，而满盈时 r1+r2-2 已达 46。
      this.currentSlime = id;
      grid.forEachNearby(slimes.x[id], slimes.y[id], slimes.radius[id] + slimes.maxActiveRadius, this.resolveNeighbour);
    }
    for (let id = 0; id < drops.capacity; id += 1) {
      if (drops.active[id] === 0) continue;
      drops.previousX[id] = drops.x[id]; drops.previousY[id] = drops.y[id];
      drops.x[id] += drops.vx[id] * dt; drops.y[id] += drops.vy[id] * dt;
      if (drops.y[id] < -40 || drops.y[id] > height + 40) {
        commands.lifecycle.push(CommandKind.DespawnDrop, -1, id);
      }
    }
    for (let id = 0; id < particles.capacity; id += 1) {
      if (particles.active[id] === 0) continue;
      particles.life[id] -= dt;
      if (particles.life[id] <= 0) { particles.release(id); continue; }
      particles.vy[id] += PARTICLE_GRAVITY * dt; particles.x[id] += particles.vx[id] * dt; particles.y[id] += particles.vy[id] * dt;
    }
  }
}
